import React, { useMemo, useState, useEffect, useCallback, useRef, memo } from "react";
import {Row, Col, Button, Nav, Offcanvas} from "react-bootstrap";
import Chat from "./Chat/Chat";
import Menu from "./Menu/Menu";
import SourceViewer from "./SourceViewer/SourceViewerNew";
import Threads from "./Threads";
import Tasks from "./Menu/Tasks";
import FileSelector from "./Menu/FileSelector/FileSelectorNew";
import type { FileSelectorProps } from "./Menu/FileSelector/FileSelectorNew";
import { pagesContent, endpointsCf } from "../../config/componentsConfig";
import usePageFilters from "../../hooks/usePageFilters";
import useIsMobile from "../../hooks/useIsMobile";
import { useChatContext } from "../../contexts/ChatContext";
import styles from "./PageContent.module.scss";
import usePromptLibraryData from "../../hooks/usePromptLibraryData";
import { useLocation } from "react-router-dom";
import { TasksProvider, useTasksContext } from "../../contexts/TasksContext";
import { ExhaustiveSearchStreamResume } from "./ExhaustiveSearchStreamResume";
import type { Thread } from "../../types/types";

const FORCE_CLOSE_PANEL_THREAD_KEY = "infravia-force-close-panel-thread-id";

const TasksNavButton = memo(function TasksNavButton({
  selectedMenu,
  onClick,
}: {
  selectedMenu: number;
  onClick: () => void;
}) {
  const { tasks } = useTasksContext();
  const n = tasks.length;
  return (
    <Button
      onClick={onClick}
      title="Tasks"
      variant={selectedMenu === 5 ? "secondary" : "outline-secondary"}
      className={styles.menuButton}
      size="sm"
      style={{ position: "relative" }}
    >
      <i className="bi bi-list-check" style={{ fontSize: "20px" }}></i>
      {/* Badge à l'intérieur du bouton : le parent du menu a overflow:hidden, les offsets négatifs étaient masqués */}
      <span
        aria-label={`${n} task${n === 1 ? "" : "s"}`}
        style={{
          position: "absolute",
          top: "2px",
          right: "2px",
          minWidth: "16px",
          height: "16px",
          padding: "0 3px",
          borderRadius: "8px",
          backgroundColor: n > 0 ? "#dc3545" : "#495057",
          color: "#fff",
          fontSize: "10px",
          lineHeight: "16px",
          textAlign: "center",
          fontWeight: 700,
          zIndex: 2,
          boxShadow: "0 0 0 1px rgba(255,255,255,0.9)",
        }}
      >
        {n > 99 ? "99+" : n}
      </span>
    </Button>
  );
});

const UnattachDocSummary = ({setSummaryView}: {setSummaryView: (summaryView: {viewed: boolean, doc: string, summary: string}) => void}) => {
  return(
    <button 
      className={styles.unattachSummaryButton} 
      onClick={() => setSummaryView({viewed: false, doc: "", summary: ""})}
      title="Unattach summary">
      <i className="bi bi-x"></i>
    </button>
  )
}

interface PageContentProps {
  page: 'home' | 'legal' | 'market' | 'mydeal' | 'oreima';
}

function PageContent({ page }: PageContentProps) {

  const fileSelectorRef = useRef<any>(null);
  const FileSelectorWithRef =
    FileSelector as unknown as React.ForwardRefExoticComponent<
      React.PropsWithoutRef<FileSelectorProps> & React.RefAttributes<any>
    >;
  const { criterias, filters, setFilters, isUpdatingCriterias } = usePageFilters(endpointsCf.filters(page), page);
  const [selectedText, setSelectedText] = useState("");
  const [selectedMode, setSelectedMode] = useState("SEARCH");
  const isMobile = useIsMobile();
  const [menuVisibility, setMenuVisibility] = useState(false);
  const [selectedMenu, setSelectedMenu] = useState(0); //0: Closed, 1: Filters and Files, 2: Chat History
  const [activeView, setActiveView] = useState<'filters' | 'files'>('filters');
  const {
    chatItems,
    changeThread,
    changeSelectedSource,
    selectedThread,
    setIsReformulation,
    removeOptimisticThoughtMessages,
    removeOptimisticUserMessages,
  } = useChatContext();
  const [IsUpdatingFiles, setIsUpdatingFiles] = useState(false);
  const MENU_SIZE = isMobile ? "100vw" : "50vw";
  const {promptLibraryData, getPromptLibraryExcel} = usePromptLibraryData();
  const pageContent = useMemo(() => pagesContent[page], [page]);
  const [summaryView, setSummaryView] = useState({viewed: false, doc: "", summary: ""});
  /** Case « Outils dev » du chat : re-synthèse finale, « Last activity » sur les cartes Tasks. */
  const [devMode, setDevMode] = useState(false);
  const selectedMenuRef = useRef(selectedMenu);

  const location = useLocation(); // Get the current location
  const queryParams = new URLSearchParams(location.search); // Parse query parameters

  // Evite d'ouvrir plusieurs fois le même thread si `threadId` reste dans l'URL.
  const lastOpenedThreadIdRef = useRef<string | null>(null);
  const forcedClosedThreadIdRef = useRef<string | null>(null);

  useEffect(() => {
    selectedMenuRef.current = selectedMenu;
  }, [selectedMenu]);

  useEffect(() => {
    if (summaryView.viewed) {
      setSelectedMenu(4);
    } else {
      setSelectedMenu(0);
    }
  }, [summaryView.viewed]);

  useEffect(() => {
    const mode = queryParams.get("mode");
    if (mode) {
      setSelectedMode(mode === "UNDEFINED" ? "SEARCH" : mode);
    }
  }, [page]);

  useEffect(() => {
    const tid = new URLSearchParams(location.search).get("threadId");
    if (!tid) {
      lastOpenedThreadIdRef.current = null;
      return;
    }
    if (lastOpenedThreadIdRef.current === tid) return;
    lastOpenedThreadIdRef.current = tid;

    // Ouvre le thread demandé (ex: depuis un clic dans “Tasks”).
    setSelectedMenu(0);
    changeThread(
      {
        id: tid,
        title: "Conversation",
        updatedAt: new Date(),
      } as Thread,
      true
    );
  }, [location.search, changeThread]);

  useEffect(() => {
    const tid = selectedThread?.id;
    if (!tid) return;
    let forcedTid: string | null = null;
    try {
      forcedTid = sessionStorage.getItem(FORCE_CLOSE_PANEL_THREAD_KEY);
    } catch {
      forcedTid = null;
    }
    if (forcedTid && forcedTid === tid) {
      forcedClosedThreadIdRef.current = tid;
      setSelectedMenu(0);
      setMenuVisibility(false);
    }
  }, [selectedThread?.id]);

  const modeOptions = useMemo(() => {
    if (!pageContent?.containers) return [];
    return pageContent.containers
      .filter((c) => c.modeValue !== "UNDEFINED")
      .map((container) => ({
        value: container.modeValue,
        icon: container.icon,
        label: container.label,
        tooltip: container.tooltip,
      }));
  }, [pageContent]);

  useEffect(() => {
    if (
      forcedClosedThreadIdRef.current &&
      selectedThread?.id === forcedClosedThreadIdRef.current
    ) {
      return;
    }
    // Ne jamais auto-basculer vers "Filters" si l'utilisateur est dans
    // l'historique (2) ou dans My Tasks (5).
    if ([2, 5].includes(selectedMenuRef.current)) {
      return;
    }
    if (["SEARCH", "MYGEORGES"].includes(selectedMode)) {
      if (!isMobile) {
        setSelectedMenu(1);
        setActiveView('filters');
      }
      if (selectedMode === "MYGEORGES") {
        if (!filters.hasOwnProperty('user_id')) {
          setFilters({});
        }
      }
  
      if (selectedMode === "SEARCH") {
        setSelectedMenu(1);
        setActiveView('filters');
      }
    }
    else {
      setSelectedMenu(0);
    }

  }, [selectedMode, isMobile]);

  const handleNewThread = useCallback(() => {
    forcedClosedThreadIdRef.current = null;
    try {
      sessionStorage.removeItem(FORCE_CLOSE_PANEL_THREAD_KEY);
    } catch {
      // ignore
    }
    changeThread(null);
    // Conserver les streams en cours pour autoriser le travail en parallèle.
    changeSelectedSource(null);
    setIsReformulation(false);
    removeOptimisticThoughtMessages();
    removeOptimisticUserMessages();
    setSelectedMode("SEARCH");
    setSelectedText("");
    setSelectedMenu(1);
    setMenuVisibility(true);
    setActiveView('filters');
  }, [
    changeThread,
    changeSelectedSource,
    setIsReformulation,
    removeOptimisticThoughtMessages,
    removeOptimisticUserMessages,
  ]);

  const handleMenuToggle = useCallback((menuType: number) => {
    if (menuType > 0) {
      forcedClosedThreadIdRef.current = null;
      try {
        sessionStorage.removeItem(FORCE_CLOSE_PANEL_THREAD_KEY);
      } catch {
        // ignore
      }
    }
    if (isMobile) {
      if (selectedMenu === menuType) {
        setSelectedMenu(0);
        setMenuVisibility(false);
      } else {
        setSelectedMenu(menuType);
        setMenuVisibility(true);
      }
    } else {
      if (selectedMenu === menuType) {
        setSelectedMenu(0);
      } else {
        setSelectedMenu(menuType);
      }
    }
  }, [selectedMenu, isMobile]);

  const handleViewChange = useCallback((view: 'filters' | 'files') => {
    setActiveView(view);
  }, []);

  const renderMenuContent = () => {
    if (selectedMenu === 2) {
      if (isMobile) {
        return (
          <div className={styles.mobileSheetBody} aria-hidden style={{ minHeight: 40 }} />
        );
      }
      return (
        <div className={styles.chatHistoryContainer}>
          <Threads setSelectedMenu={setSelectedMenu} setMenuVisibility={setMenuVisibility} />
        </div>
      );
    }

    if (isMobile && selectedMenu === 5) {
      return (
        <div className={styles.mobileSheetBody} aria-hidden style={{ minHeight: 40 }} />
      );
    }

    return (
      <>
        {isMobile && (selectedMode !== "MYGEORGES" && page !== "oreima") && (
          <div className={styles.menuNav}>
            <Nav variant="pills" className="mb-3">
              <Nav.Item>
                <Nav.Link 
                  active={activeView === 'filters'} 
                  onClick={() => handleViewChange('filters')}
                  className={styles.navLink}
                >
                  Filters
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link 
                  active={activeView === 'files'} 
                  onClick={() => handleViewChange('files')}
                  className={styles.navLink}
                >
                  Files
                </Nav.Link>
              </Nav.Item>
            </Nav>
          </div>
        )}
        
        <div className={styles.menuContent}>
          <div style={{ display: activeView === 'filters' ? 'block' : 'none', height: "100%" }}>
            <Menu 
              page={page}
              selectedMenu={selectedMenu}
              criterias={criterias}
              filters={filters}
              setFilters={setFilters}
              setSelectedMenu={setSelectedMenu}
              setMenuVisibility={setMenuVisibility}
              isUpdatingCriterias={isUpdatingCriterias}
              selectedMode={selectedMode}
              setSelectedMode={setSelectedMode}
              promptLibraryData={promptLibraryData}
              summaryView={summaryView}
              setSummaryView={setSummaryView}
              fileSelectorRef={fileSelectorRef}
              devMode={devMode}
            />
          </div>
          <div style={{ display: activeView === 'files' ? 'block' : 'none', height: "100%" }}>
            <div className={styles.fileSelectorContainer}>
              <FileSelectorWithRef 
                ref={fileSelectorRef}
                setFilters={setFilters}
                filters={filters}
                setSelectedMenu={setSelectedMenu}
                page={page}
                selectedMode={selectedMode}
                isUpdatingCriterias={isUpdatingCriterias}
                IsUpdatingFiles={IsUpdatingFiles}
                setIsUpdatingFiles={setIsUpdatingFiles}
                setSummaryView={setSummaryView}
              />
            </div>
          </div>
        </div>
      </>
    );
  };

  // 3. Vérification après tous les hooks
  if (!pageContent) {
    console.error(`No content found for page: ${page}`);
    return null;
  }

  return (
    <TasksProvider page={page} fileSelectorRef={fileSelectorRef} selectedMode={selectedMode} selectedMenu={selectedMenu}>
    <>
      <ExhaustiveSearchStreamResume page={page} />
      <Row id="container" className="h-100 mh-100 overflow-hidden p-0">
        <div
          style={{
            width: Boolean(selectedMenu > 0) ? MENU_SIZE : "45px",
            display: "flex",
            flexDirection: "row",
            padding: 0,
            transition: `width 0.3s var(--georges-ease-out, ease-out), background-color 0.25s ease`,
            height: "100%",
            backgroundColor: menuVisibility ? "#F6F6F6" : "transparent", 
            overflow: "hidden",
            zIndex: 1000,
            position: "relative"
          }}
          onTransitionEnd={() => setMenuVisibility(Boolean(selectedMenu > 0))}
        >
          <div style={{ 
                height: "fit-content",
                alignSelf: "flex-start",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                gap: "10px",
                marginTop: "10px",
                marginLeft: "5px",
                zIndex: 1001
              }}>
              <Button
                onClick={handleNewThread}
                title={"New Chat"}
                variant={"outline-secondary"}
                className={styles.menuButton}
                size="sm">
                <i className="bi bi-chat" style={{ fontSize: "20px" }}></i>
              </Button>
              <Button
                onClick={() => handleMenuToggle(2)}
                title={"Chat History"}
                variant={selectedMenu === 2 ? "secondary" : "outline-secondary"}
                className={styles.menuButton}
                size="sm">
                <i className="bi bi-chat-left-text" style={{ fontSize: "20px" }}></i>
              </Button>
              {!isMobile && (page === "market" || page ==="mydeal") && selectedMode !== "MYGEORGES" && (
                <Button
                onClick={() => {
                  handleMenuToggle(3);
                  getPromptLibraryExcel();
                }}
                title={"Prompts Library"}
                variant={selectedMenu === 3 ? "secondary" : "outline-secondary"}
                className={styles.menuButton}
                size="sm">
                <i className="bi bi-book" style={{ fontSize: "20px" }}></i>
              </Button>)}
              {["SEARCH", "MYGEORGES"].includes(selectedMode) &&
                <Button
                  onClick={() => handleMenuToggle(1)}
                  title={"Filter and select documents"}
                  variant={selectedMenu === 1 ? "secondary" : "outline-secondary"}
                  className={styles.menuButton}
                  style={{color: "#00afcd"}}
                  size="sm">
                  <i className="bi bi-menu-button-wide" ></i>
                </Button>}
              {summaryView.viewed && (
                <Button
                onClick={() => {
                  handleMenuToggle(4);
                }}
                title={`View ${summaryView.doc} summary`}
                variant={selectedMenu === 4 ? "secondary" : "outline-secondary"}
                className={styles.menuButton}
                style={{border: "1px solid #515151"}}
                size="sm">
                <i className="bi bi-file-earmark-text" style={{ fontSize: "20px" }}></i>
                {selectedMenu !== 0 && <UnattachDocSummary setSummaryView={setSummaryView} />}
              </Button>)}
              <TasksNavButton
                selectedMenu={selectedMenu}
                onClick={() => handleMenuToggle(5)}
              />
          </div>
          <div style={{
            position: "absolute",
            top: 0,
            left: "45px",
            right: 0,
            bottom: 0,
            transform: menuVisibility ? "translateX(0)" : "translateX(-100%)",
            transition: `transform 0.3s var(--georges-ease-out, ease-out), opacity 0.25s ease`,
            height: "100%",
            width: "calc(100% - 45px)",
            backgroundColor: "#F6F6F6",
            opacity: menuVisibility ? 1 : 0
          }}>
            {!isMobile && selectedMenu > 0 && (
              <Button
                variant="light"
                className={styles.panelCloseButton}
                onClick={() => {
                  setSelectedMenu(0);
                  setMenuVisibility(false);
                  setActiveView("filters");
                }}
                title="Fermer le panneau et afficher en plein écran"
                aria-label="Fermer le panneau"
              >
                <i className="bi bi-x-lg" aria-hidden="true" />
              </Button>
            )}
            {renderMenuContent()}
          </div>
        </div>
        <Offcanvas
          show={isMobile && (selectedMenu === 2 || selectedMenu === 5)}
          onHide={() => {
            setSelectedMenu(0);
            setMenuVisibility(false);
          }}
          placement="bottom"
          className={`${styles.georgesMobileSheet} d-md-none`}
        >
          <Offcanvas.Header closeButton className="border-bottom py-2">
            <Offcanvas.Title className="h6 mb-0">
              {selectedMenu === 2 ? "Chat History" : "My Tasks"}
            </Offcanvas.Title>
          </Offcanvas.Header>
          <Offcanvas.Body className="d-flex flex-column p-0">
            {selectedMenu === 2 ? (
              <div className={styles.mobileSheetBody}>
                <Threads setSelectedMenu={setSelectedMenu} setMenuVisibility={setMenuVisibility} />
              </div>
            ) : null}
            {selectedMenu === 5 ? (
              <div className={styles.mobileSheetBody} style={{ overflow: "auto" }}>
                <Tasks
                  page={page}
                  selectedMode={selectedMode}
                  setSelectedMenu={setSelectedMenu}
                  setSelectedMode={setSelectedMode}
                  setSummaryView={setSummaryView}
                  devMode={devMode}
                />
              </div>
            ) : null}
          </Offcanvas.Body>
        </Offcanvas>
        <Col className="h-100 overflow-auto">
          {(!isMobile || !menuVisibility) && (
            <Chat
              page={page as any}
              filters={filters}
              setFilters={setFilters}
              selectedText={selectedText}
              selectedMode={selectedMode}
              selectedMenu={selectedMenu}
              setSelectedMenu={setSelectedMenu}
              setSelectedText={setSelectedText}
              setSelectedMode={setSelectedMode}
              modeOptions={modeOptions}
              setIsUpdatingFiles={setIsUpdatingFiles}
              devMode={devMode}
              setDevMode={setDevMode}
            />
          )}
        </Col>
      </Row>
      {/* Modal Component */}
      <SourceViewer 
      page={page as any} 
      selectedMode={selectedMode}
      />
    </>
    </TasksProvider>
  );
}

export default PageContent;